import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useEventData } from './hooks/useEventData';
import LoadingSkeleton from './components/LoadingSkeleton';
import EventHeader from './components/EventHeader';
import OverviewTab from './tabs/OverviewTab';
import ChartsTab from './tabs/ChartsTab';
import SignalsTab from './tabs/SignalsTab';
import MoneylineTab from './tabs/MoneylineTab';
import SpreadTab from './tabs/SpreadTab';
import TotalsTab from './tabs/TotalsTab';

type TabId = 'overview' | 'moneyline' | 'spread' | 'totals' | 'charts' | 'signals';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
}

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: '📊' },
  { id: 'moneyline', label: 'Moneyline', icon: '🎯' },
  { id: 'spread', label: 'Spread', icon: '📐' },
  { id: 'totals', label: 'Over/Under', icon: '🎲' },
  { id: 'charts', label: 'Charts', icon: '📈' },
  { id: 'signals', label: 'Signals', icon: '📡' },
];

export default function EventDetail() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const {
    // Core data
    event,
    polymarket,
    signals,
    opportunities,
    lifecycleData,
    bettingVolumeData,
    // Chart data
    lineChartData,
    liveOdds,
    bookmakersInData,
    polymarketChartData,
    priceGapChartData,
    signalChartData,
    unifiedChartResult,
    // Computed values
    polymarketPrice,
    polymarketChange24h,
    lastOddsUpdate,
    priceGap,
    bestOpportunity,
    sharpBooks,
    squareBooks,
    // State
    selectedBookmakers,
    toggleBookmaker,
    // Query state
    isLoading,
    error,
    isFetching,
    refetch,
  } = useEventData(eventId);

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error || !event) {
    const errorMessage = error?.message || 'Unknown error';
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white flex items-center gap-2"
        >
          ← Back
        </button>
        <div className="card text-center py-12">
          <p className="text-red-400">Event not found</p>
          <p className="text-gray-500 text-sm mt-2">Error: {errorMessage}</p>
          <p className="text-gray-600 text-xs mt-1">Event ID: {eventId}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button and Refresh */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white flex items-center gap-2"
        >
          ← Back
        </button>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
            isFetching
              ? 'border-gray-700 bg-surface-50 text-gray-500 cursor-not-allowed'
              : 'border-gray-600 bg-surface-100 text-gray-300 hover:bg-surface-200 hover:border-gray-500'
          }`}
        >
          <span className={isFetching ? 'animate-spin' : ''}>🔄</span>
          {isFetching ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Event Header - Always visible */}
      <EventHeader event={event} polymarket={polymarket} />

      {/* Tab Navigation */}
      <div className="border-b border-gray-800">
        <div className="flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-purple-500 text-purple-400 bg-purple-500/10'
                  : 'border-transparent text-gray-400 hover:text-white hover:border-gray-600'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {/* Show badge for signals tab if there are active signals */}
              {tab.id === 'signals' && signals.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded-full">
                  {signals.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <OverviewTab
            event={event}
            polymarket={polymarket}
            polymarketPrice={polymarketPrice}
            polymarketChange24h={polymarketChange24h}
            lastOddsUpdate={lastOddsUpdate}
            priceGap={priceGap}
            lifecycleData={lifecycleData}
            bettingVolumeData={bettingVolumeData}
            unifiedChartResult={unifiedChartResult}
          />
        )}

        {activeTab === 'moneyline' && (
          <MoneylineTab
            bettingVolumeData={bettingVolumeData}
            homeTeam={event.homeTeam}
            awayTeam={event.awayTeam}
            lineMovement={bettingVolumeData?.lineMovement ?? null}
          />
        )}

        {activeTab === 'spread' && (
          <SpreadTab
            bettingVolumeData={bettingVolumeData}
            homeTeam={event.homeTeam}
            awayTeam={event.awayTeam}
          />
        )}

        {activeTab === 'totals' && (
          <TotalsTab
            bettingVolumeData={bettingVolumeData}
            homeTeam={event.homeTeam}
            awayTeam={event.awayTeam}
          />
        )}

        {activeTab === 'charts' && (
          <ChartsTab
            event={event}
            lineChartData={lineChartData}
            liveOdds={liveOdds}
            bookmakersInData={bookmakersInData}
            polymarketChartData={polymarketChartData}
            priceGapChartData={priceGapChartData}
            signalChartData={signalChartData}
            selectedBookmakers={selectedBookmakers}
            toggleBookmaker={toggleBookmaker}
            sharpBooks={sharpBooks}
            squareBooks={squareBooks}
            lastOddsUpdate={lastOddsUpdate}
            signals={signals}
          />
        )}

        {activeTab === 'signals' && (
          <SignalsTab
            signals={signals}
            opportunities={opportunities}
            bestOpportunity={bestOpportunity}
          />
        )}
      </div>
    </div>
  );
}
